import { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ChronologyListItem from './ListItem';
import { SubToolbar } from '../../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../../components/TableWrapper';
import { useGetChronologies } from '@/api/queries/useGetChronologies';
import EmptyStateCreation from '@/components/molecules/EmptyStateCreation';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { useGeneralPagination } from '@/hooks/useGeneralPagination';
import DataViewToolbar from '@/screens/Chronos/CaseEditor/DataView/components/TableToolbar';
import { Chronology } from '@/types';

interface ChronologyListProps {
  caseId: string | null;
}

const ChronologyList = ({ caseId }: ChronologyListProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialPageSize = 20;
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const { pagination, setPagination } = useGeneralPagination({ pageSize: initialPageSize });

  const {
    data: responseChronologies,
    isFetching: isLoadingChronologies,
    refetch: refetchCaseChronologies,
  } = useGetChronologies({
    caseId: caseId ?? '',
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const noOfPages = responseChronologies?.pageCount ?? 0;

  const onSearchCall = () => {
    refetchCaseChronologies();
  };

  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: initialPageSize };
    setPagination(newPaginationState);
    searchParams.set('page', String(newPaginationState.pageIndex + 1));
    navigate(location.pathname + '?' + searchParams.toString());
  };

  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);
  const canGetPrevPage = pagination.pageIndex > 0;
  const canGetNextPage = pagination.pageIndex < (responseChronologies?.totalCount ?? 0) / initialPageSize - 1;

  return (
    <div className="flex h-full flex-col">
      <DataViewToolbar
        titleSection={<h1 className="text-lg font-semibold">Chronologies</h1>}
        inlineSearchBar={
          <SearchBox
            value={searchQuery}
            placeholder="Search chronologies"
            onChange={setSearchQuery}
            onSearchCall={onSearchCall}
          />
        }
        pagination={
          <Pagination
            canGetPrevPage={canGetPrevPage}
            canGetNextPage={canGetNextPage}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={pagination.pageIndex}
            noOfPages={noOfPages}
            isLoadingFacts={isLoadingChronologies}
            goToPage={goToPage}
          />
        }
      />
      <SubToolbar
        entityType="chronologies"
        totalCount={responseChronologies?.totalCount}
        totalUnfilteredCount={responseChronologies?.totalUnfilteredCount}
      />

      <TableWrapper>
        <div className="h-full overflow-y-auto">
          {isLoadingChronologies ? (
            <div className="flex h-full w-full items-center justify-center">
              <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
            </div>
          ) : responseChronologies?.chronologies && responseChronologies?.chronologies.length ? (
            <div className="max-w-full pr-2">
              {responseChronologies.chronologies.map((chronology: Chronology) => {
                return <ChronologyListItem chronology={chronology} key={chronology.id} />;
              })}
            </div>
          ) : (
            <EmptyStateCreation
              title="No chronologies found"
              subtitle="To make your first chronology, select the facts you want to include on the facts table."
              onClick={() => {
                navigate(`/app/chronos/case-editor/data/facts?caseId=${caseId}`);
              }}
            />
          )}
        </div>
      </TableWrapper>
    </div>
  );
};

export default ChronologyList;
